import { useCallback, useEffect, useMemo, useState } from 'react'

import { useAPPTranslation } from '~/i18n/useAPPTranslation'

import {
  identityKey,
  isValidGtin,
  parseProductCodes,
  productKey
} from '../../../helpers/gtin'
import { ADVERTISER_BRAND_LIST_LIMIT } from '../../../hooks/advertiserBrandQueryKeys'
import {
  NAME_SEARCH_MIN_LENGTH,
  useAdvertiserBrandCatalogBatchSearch,
  useAdvertiserBrandCatalogNameSearch,
  useAdvertiserBrandProducts
} from '../../../hooks/useAdvertiserBrandProducts'
import { useAdvertiserBrandSuggestions } from '../../../hooks/useAdvertiserBrandSuggestions'
import styles from '../styles.module.css'
import { BATCH_MAX_CODES, BatchPanel } from './BatchPanel'
import { CandidateList } from './CandidateList'
import { NameSearchPanel } from './NameSearchPanel'
import { PickerTabs } from './PickerTabs'
import {
  PickerMode,
  ProductCandidate,
  ProductSelection,
  toCandidate
} from './types'

type ProductPickerProps = {
  open: boolean
  selected: ProductSelection
  onSelectedChange: (next: ProductSelection) => void
  /** Candidatos do catálogo que casam com uma marca já cadastrada, na busca sem termo. */
  showSuggestions?: boolean
  /** Falso quando a marca é decidida fora do picker: o CSV não leva a coluna Marca para a lista. */
  withBrands?: boolean
  /** Conteúdo à direita da linha; não é chamado para produto já vinculado. */
  renderAside?: (row: ProductCandidate) => React.ReactNode
}

/** O catálogo pode ter GTINs mal-formados; o mesmo dígito verificador do back. Item por SKU não tem EAN. */
const isValidHit = (hit: { identifier_type: string; gtin: string | null }) =>
  hit.identifier_type === 'sku' || isValidGtin(hit.gtin ?? '')

/**
 * Busca no catálogo dos publishers vinculados (por nome ou lote de EANs) e
 * devolve os produtos escolhidos.
 */
/** Mesma espera da busca por nome em `useAdvertiserBrandProducts`. */
const BATCH_PARSE_DEBOUNCE = 400

export const ProductPicker: React.FC<ProductPickerProps> = ({
  open,
  selected,
  onSelectedChange,
  showSuggestions = true,
  withBrands = true,
  renderAside
}) => {
  const { t } = useAPPTranslation()

  const [mode, setMode] = useState<PickerMode>('search')
  const [searchTerm, setSearchTerm] = useState('')
  const [rawGtins, setRawGtins] = useState('')
  // marca de cada código vinda da coluna Marca de um CSV enviado
  const [fileBrands, setFileBrands] = useState<Record<string, string>>({})

  const hasSearchTerm = !!searchTerm.trim()

  const nameSearch = useAdvertiserBrandCatalogNameSearch(searchTerm, {
    enabled: open
  })

  const { data: suggestions, isFetching: isLoadingSuggestions } =
    useAdvertiserBrandSuggestions({
      enabled: open && showSuggestions && !hasSearchTerm
    })

  // o textarea responde na hora; o parse da colagem inteira espera a digitação parar
  const [debouncedRawGtins, setDebouncedRawGtins] = useState(rawGtins)

  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedRawGtins(rawGtins),
      BATCH_PARSE_DEBOUNCE
    )

    return () => clearTimeout(timer)
  }, [rawGtins])

  // a marca do arquivo só vale enquanto ela ainda está no texto da caixa
  const activeFileBrands = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(fileBrands).filter(([, brand]) =>
          debouncedRawGtins.includes(brand)
        )
      ),
    [debouncedRawGtins, fileBrands]
  )
  const parsedCodes = useMemo(
    () => parseProductCodes(debouncedRawGtins, Object.values(activeFileBrands)),
    [debouncedRawGtins, activeFileBrands]
  )
  const batchSearch = useAdvertiserBrandCatalogBatchSearch(parsedCodes)
  const isBatchOverLimit =
    parsedCodes.gtins.length > BATCH_MAX_CODES ||
    parsedCodes.skus.length > BATCH_MAX_CODES

  /**
   * Editar a lista troca a chave da busca e zera o resultado na tela: o que ela
   * marcou sai junto. `selected` nas deps desfaria a seleção da própria busca.
   */
  useEffect(() => {
    const fromBatch = Object.keys(selected).filter(
      key => selected[key].origin === 'batch'
    )

    if (!fromBatch.length) return

    const next = { ...selected }

    fromBatch.forEach(key => delete next[key])
    onSelectedChange(next)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parsedCodes])

  // a rota não filtra por lista de GTINs: acima do teto, "já adicionado" deixa de ser completo
  const { data: existingProducts } = useAdvertiserBrandProducts(
    { quantity: ADVERTISER_BRAND_LIST_LIMIT },
    { enabled: open }
  )

  const existingByKey = useMemo(
    () =>
      (existingProducts?.data ?? []).reduce<Record<string, string | null>>(
        (acc, product) => {
          acc[productKey(product)] = product.brand

          return acc
        },
        {}
      ),
    [existingProducts?.data]
  )

  /**
   * A checagem de vínculo chega depois da lista: o que ela alcançar sai da
   * seleção, para não reenviar à API um produto que a linha já mostra travado.
   */
  useEffect(() => {
    const alreadyLinked = Object.keys(selected).filter(
      key => key in existingByKey
    )

    if (!alreadyLinked.length) return

    const next = { ...selected }

    alreadyLinked.forEach(key => delete next[key])
    onSelectedChange(next)
  }, [existingByKey, onSelectedChange, selected])

  // resultado de busca entra sem marca: só a lista de sugestões vem pré-preenchida
  const foundRows = useMemo(
    () =>
      nameSearch.items
        .filter(isValidHit)
        .map(product => toCandidate(product, 'search')),
    [nameSearch.items]
  )

  /**
   * `enabled: false` barra a busca, não a leitura do cache: sem esse recorte,
   * o modal de nova marca herda as sugestões já carregadas em outra tela.
   */
  const suggestedRows = useMemo(() => {
    if (!showSuggestions) return []

    return (suggestions ?? []).filter(isValidHit).map(item => {
      const hit = {
        identifier_type: item.identifier_type,
        gtin: item.gtin,
        product_sku: item.product_sku,
        publisher: item.publisher,
        name: item.product_name,
        image_url: item.image_url
      }

      return toCandidate(hit, 'search', item.suggested_brand)
    })
  }, [suggestions, showSuggestions])

  /** A marca da linha acompanha o produto achado pelo EAN ou pelo SKU daquele código. */
  const toBatchCandidate = useCallback(
    (item: AdvertiserBrandCatalogSearchItem) => {
      // a marca colada na linha vale mais que a do arquivo
      const brandOf = (code: string | null) =>
        (code && (parsedCodes.brands[code] ?? activeFileBrands[code])) || null
      const codes = [
        item.gtin,
        item.product_sku,
        ...(batchSearch.aliases?.[
          identityKey({
            gtin: item.gtin,
            sku: item.product_sku,
            publisherId: item.publisher?.id ?? null
          })
        ] ?? [])
      ]

      return toCandidate(
        item,
        'batch',
        null,
        codes.map(brandOf).find(Boolean) ?? null
      )
    },
    [activeFileBrands, parsedCodes.brands, batchSearch.aliases]
  )

  /** Só o que existe no catálogo dos publishers vira candidato. */
  const batchRows = useMemo(
    () => (batchSearch.items ?? []).map(toBatchCandidate),
    [batchSearch.items, toBatchCandidate]
  )

  const batchSummary = useMemo(() => {
    if (!batchSearch.items) return undefined

    // o código colado casa com o EAN ou com o SKU de algum item devolvido,
    // mesmo quando o produto repetido não entrou na lista
    const found = new Set(batchSearch.matched)

    return {
      found: batchSearch.items.length,
      notFound: parsedCodes.codes.filter(code => !found.has(code))
    }
  }, [batchSearch.items, batchSearch.matched, parsedCodes])

  /**
   * Encontrados já entram marcados; sem correspondência ficam para o usuário
   * decidir. O mesmo SKU em mais de um publisher são produtos diferentes: ficam
   * na lista, desmarcados, para a pessoa escolher.
   */
  const handleBatchSearch = async () => {
    const items = await batchSearch.search()
    const next = { ...selected }

    // um SKU colado que acha produtos diferentes é ambíguo, com ou sem EAN no
    // item, a menos que o EAN do item também tenha sido colado; um código com
    // formato de EAN também é buscado como SKU
    const skuOnly = new Set(parsedCodes.skus)
    const matchesBySku = items.reduce<Record<string, number>>((acc, item) => {
      if (item.product_sku && skuOnly.has(item.product_sku))
        acc[item.product_sku] = (acc[item.product_sku] ?? 0) + 1

      return acc
    }, {})

    items.forEach(item => {
      const candidate = toBatchCandidate(item)
      const isAmbiguous =
        !!item.product_sku &&
        matchesBySku[item.product_sku] > 1 &&
        !(item.gtin && parsedCodes.gtins.includes(item.gtin))

      if (!(candidate.key in existingByKey) && !isAmbiguous)
        next[candidate.key] = candidate
    })

    onSelectedChange(next)
  }

  /** As sugestões são da lista sem termo: com termo curto elas não respondem a ele. */
  const isTermTooShort = hasSearchTerm && !nameSearch.isActive

  const searchTabRows = nameSearch.isActive ? foundRows : suggestedRows
  const searchListKey = nameSearch.isActive
    ? 'rm:advertiserProducts.addModal.list.found'
    : 'rm:advertiserProducts.addModal.list.suggested'

  const isSearchEmpty =
    nameSearch.isActive && !nameSearch.isFetching && !foundRows.length
  const hasNoSuggestions =
    showSuggestions &&
    !hasSearchTerm &&
    !isLoadingSuggestions &&
    !suggestedRows.length

  return (
    <div className={styles.picker}>
      <PickerTabs mode={mode} onChange={setMode} />

      {mode === 'search' ? (
        <NameSearchPanel
          value={searchTerm}
          loading={nameSearch.isFetching || isLoadingSuggestions}
          onChange={setSearchTerm}
        />
      ) : (
        <BatchPanel
          rawGtins={rawGtins}
          onRawGtinsChange={setRawGtins}
          withBrands={withBrands}
          onFileBrandsChange={brands =>
            setFileBrands(previous => ({ ...previous, ...brands }))
          }
          parsedCount={parsedCodes.codes.length}
          isOverLimit={isBatchOverLimit}
          isSearching={batchSearch.isFetching}
          onSearch={handleBatchSearch}
          summary={batchSummary}
        />
      )}

      <div className={styles.pickerList}>
        {mode === 'search' ? (
          <>
            {!isTermTooShort && (
              <CandidateList
                rows={searchTabRows}
                label={t(searchListKey, { count: searchTabRows.length })}
                selected={selected}
                onSelectedChange={onSelectedChange}
                existingByKey={existingByKey}
                renderAside={renderAside}
              />
            )}

            {isTermTooShort && (
              <p className={styles.pickerHint}>
                {t('rm:advertiserProducts.addModal.search.minLength', {
                  min: NAME_SEARCH_MIN_LENGTH
                })}
              </p>
            )}

            {isSearchEmpty && (
              <p className={styles.pickerHint}>
                {t('rm:advertiserProducts.addModal.search.empty', {
                  query: searchTerm.trim()
                })}
              </p>
            )}

            {hasNoSuggestions && (
              <p className={styles.pickerHint}>
                {t('rm:advertiserProducts.addModal.suggestions.empty')}
              </p>
            )}
          </>
        ) : (
          <>
            <CandidateList
              rows={batchRows}
              label={t('rm:advertiserProducts.addModal.list.found', {
                count: batchRows.length
              })}
              selected={selected}
              onSelectedChange={onSelectedChange}
              existingByKey={existingByKey}
              renderAside={renderAside}
            />
            {!batchSearch.items && (
              <p className={styles.pickerHint}>
                {t('rm:advertiserProducts.addModal.batch.hint')}
              </p>
            )}
          </>
        )}
      </div>
    </div>
  )
}
